// Door sensors (jobq-api "After-hours security: door sensors", since 2026-10-23) and the crew's
// own check-in (POST /security/checkin, 2026-10-25).
//
// The Konnected board reports each door to jobq-api; JobQ decides with the camera watch's armed
// answer whether an open door is an alert. This module is the ONE copy of the reads, writes and
// wording for the Doors screen (pages/DoorsPage.jsx) and the Check-in screen (pages/CheckInPage.jsx).
//
// - Every Admin call goes through securityFetch (the session's X-Admin-Pin).
// - The check-in is NOT an Admin call: any active employee types their own PIN, sent as
//   X-Checkin-Pin. It goes through beaconFetch (config.js's fetch wrapper adds X-API-Key) and never
//   carries X-Admin-Pin: a check-in disarms the alarm, so it must be the typed PIN of the person here.

import { beaconFetch } from '../../utils/beacons';
import { securityFetch } from '../../utils/securityApi';
import { cameraSlug } from '../../utils/cameraRelay';
import { shopClock } from '../../utils/shopDate';

// ── Reads / writes ──────────────────────────────────────────────────────────

export const DOORS_HOURS = 48;
export const DOORS_LIMIT = 200;
export const DOORS_POLL_MS = 30000;

export const fetchDoors = () => securityFetch(`/security/doors?hours=${DOORS_HOURS}&limit=${DOORS_LIMIT}`);

export const setDoorsMode = (mode) =>
  securityFetch('/security/doors/mode', { method: 'POST', body: JSON.stringify({ mode }) });

/** PUT one zone: `body` holds only what changed ({name} and/or {camera_keys}; [] clears the cameras). */
export const saveDoorZone = (panelId, zoneNumber, body) =>
  securityFetch(`/security/door_panels/${panelId}/zones/${zoneNumber}`, { method: 'PUT', body: JSON.stringify(body) });

/** An employee's I'm here ('arrive') / I'm leaving ('leave') with their own typed PIN. */
export const sendCheckin = (event, pin) =>
  beaconFetch('/security/checkin', {
    method: 'POST',
    headers: { 'X-Checkin-Pin': String(pin || '') },
    body: JSON.stringify({ event }),
  });

// ── The switch ──────────────────────────────────────────────────────────────

export const DOOR_MODES = Object.freeze([
  { mode: 'record', label: 'Record', what: 'Stores door changes only. Nobody is told.' },
  { mode: 'slack', label: 'Slack', what: 'Alerts go to Slack only. No texts, no pushes.' },
  { mode: 'live', label: 'Live', what: 'Alerts are texted and pushed, and go to Slack.' },
]);

export const modeInfo = (mode) => DOOR_MODES.find((m) => m.mode === mode) || null;

/** Switching to anything but live turns texts off: ask first. */
export const modeNeedsConfirm = (mode) => mode !== 'live';

export function modeConfirmText(mode) {
  const m = modeInfo(mode);
  return `Switch door alerts to ${m ? m.label : mode}? ${m ? m.what : ''} Nobody will be texted about an open door.`.replace(/\s+/g, ' ').trim();
}

// ── Cameras ─────────────────────────────────────────────────────────────────

export const MAX_DOOR_CAMERAS = 3;

// The API's own names (jobq-api security_doors.CAMERA_NAMES); every other camera is its station,
// with " N+1" after the first.
const DOOR_CAMERA_NAMES = {
  'laser-0': 'Laser bed',
  'laser-1': 'Laser operator',
  'laser-2': 'Laser outfeed',
  'blast-0': 'Blast',
  'blast-1': 'Blast 2',
};

const titleCase = (slug) => String(slug).split('-').filter(Boolean)
  .map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

/** 'laser-2' -> 'Laser outfeed', 'metal-room-0' -> 'Metal Room', 'brake-1' -> 'Brake 2'. */
export function doorCameraName(key, station = null) {
  if (DOOR_CAMERA_NAMES[key]) return DOOR_CAMERA_NAMES[key];
  const m = /^(.+)-(\d+)$/.exec(String(key || ''));
  if (!m) return String(key || '');
  const name = station || titleCase(m[1]);
  const idx = Number(m[2]);
  return idx === 0 ? name : `${name} ${idx + 1}`;
}

/**
 * The cameras a door can use: the relay's GET /cameras/config ({station: count}) as
 * [{key, label}] in the relay's order (key = night watch's `<slug>-<index>`), plus any key a door
 * already has that the relay no longer lists (so saving never drops it unseen).
 */
export function doorCameraChoices(config, keep = []) {
  const out = [];
  const seen = new Set();
  for (const [station, count] of Object.entries(config || {})) {
    const n = Number(count) || 0;
    for (let i = 0; i < n; i++) {
      const key = cameraSlug(station, i);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ key, label: doorCameraName(key, station) });
    }
  }
  for (const key of keep || []) {
    if (key && !seen.has(key)) {
      seen.add(key);
      out.push({ key, label: doorCameraName(key) });
    }
  }
  return out;
}

/** Tick / untick one camera, keeping the order picked, never more than MAX_DOOR_CAMERAS. */
export function toggleCamera(keys, key) {
  const list = Array.isArray(keys) ? keys : [];
  if (list.includes(key)) return list.filter((k) => k !== key);
  if (list.length >= MAX_DOOR_CAMERAS) return list;
  return [...list, key];
}

/** The PUT body for an edit: only what changed, or null when nothing did. */
export function zoneEditBody(zone, name, cameraKeys) {
  const body = {};
  const n = String(name || '').trim();
  if (n && n !== zone.name) body.name = n;
  const before = zone.camera_keys || [];
  const after = cameraKeys || [];
  if (before.length !== after.length || before.some((k, i) => k !== after[i])) body.camera_keys = after;
  return Object.keys(body).length ? body : null;
}

// ── Wording ─────────────────────────────────────────────────────────────────

/** "12 s ago", "3 min ago", "2 h ago", else the shop's clock. '' when unknown. */
export function ageText(iso, now = new Date()) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const s = Math.max(0, Math.round((now.getTime() - t) / 1000));
  if (s < 90) return `${s} s ago`;
  if (s < 90 * 60) return `${Math.round(s / 60)} min ago`;
  if (s < 24 * 3600) return `${Math.round(s / 3600)} h ago`;
  return shopClock(iso, now);
}

const latest = (...isos) => isos.filter(Boolean).sort((a, b) => new Date(b) - new Date(a))[0] || null;

/** The board's header line: {online, tone, text}. */
export function panelLine(panel, now = new Date()) {
  const last = latest(panel.last_beat_at, panel.last_event_at);
  const bits = [panel.online ? 'Online' : 'OFFLINE'];
  if (panel.firmware) bits.push(`firmware ${panel.firmware}`);
  bits.push(last ? `last report ${ageText(last, now)}` : 'it has never reported');
  if (panel.net) bits.push(panel.net === 'wifi' ? 'Wi-Fi' : 'wired');
  return { online: !!panel.online, tone: panel.online ? 'ok' : 'bad', text: `${panel.name}: ${bits.join(' · ')}` };
}

/** A door's state as a badge: OPEN (red) / Closed (green) / Unknown (grey), and since when. */
export function doorStateView(zone, now = new Date()) {
  const since = zone.state_since ? shopClock(zone.state_since, now) : '';
  if (zone.state === 'open') return { label: 'OPEN', tone: 'bad', since };
  if (zone.state === 'closed') return { label: 'Closed', tone: 'ok', since };
  return { label: 'Unknown', tone: 'muted', since: '' };
}

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** Armed or why not, for an opening ('' for a closing: armed only matters when a door opens). */
export function armedText(event) {
  if (event.state !== 'open' || event.armed === null || event.armed === undefined) return '';
  const note = String(event.armed_note || '').trim();
  if (event.armed) return note && note !== 'armed' ? cap(note) : 'Armed';
  return note ? `Not armed: ${note}` : 'Not armed';
}

/**
 * What the hold came to, in plain words: {text, tone} or null when there was no hold (a closing,
 * or an opening while not armed). tone: warn (still waiting), bad (alert sent), ok (cleared),
 * muted (covered by another door's alert).
 */
export function holdOutcome(event, holdSeconds = 120) {
  const min = Math.max(1, Math.round((Number(holdSeconds) || 120) / 60));
  switch (event.hold_state) {
    case 'pending': {
      const until = event.hold_until ? ` (until ${shopClock(event.hold_until)})` : '';
      return { tone: 'warn', text: `Waiting ${min} min for a check-in${until}` };
    }
    case 'alerted': {
      const who = [];
      if (event.texted) who.push(`texted ${event.texted}`);
      if (event.pushed) who.push(`pushed to ${event.pushed}`);
      const why = event.resolution ? ` (${event.resolution})` : '';
      return { tone: 'bad', text: `Alert sent${why}: ${who.length ? who.join('; ') : 'no texts or pushes went out'}` };
    }
    case 'cleared':
      return { tone: 'ok', text: cap(event.resolution || 'cleared') };
    case 'covered':
      return { tone: 'muted', text: 'Part of the alert on another door' };
    default:
      return null;
  }
}

/** Keeps each event's photo link for `maxAgeMs` (a fresh presigned link every 30 s would reload
 *  every picture each poll), taking the new one once it is older. Returns {event_id: {url, got}}. */
export function stablePhotoUrls(prev, events, nowMs = Date.now(), maxAgeMs = 10 * 60 * 1000) {
  const out = {};
  for (const e of events || []) {
    if (!e || !e.photo_url) continue;
    const old = prev && prev[e.event_id];
    out[e.event_id] = old && nowMs - old.got < maxAgeMs ? old : { url: e.photo_url, got: nowMs };
  }
  return out;
}

// ── Check-in ────────────────────────────────────────────────────────────────

export const PIN_MIN = 4;
export const PIN_MAX = 12;

/** Digits only, at most PIN_MAX. */
export const cleanPin = (raw) => String(raw || '').replace(/\D/g, '').slice(0, PIN_MAX);
export const pinReady = (pin) => /^\d+$/.test(pin || '') && pin.length >= PIN_MIN && pin.length <= PIN_MAX;

/** The answer to a check-in, as {tone: 'ok'|'bad', lines: [...]}. */
export function checkinAnswer(res, event) {
  if (res && res.ok) {
    const b = res.body || {};
    const person = b.person || 'you';
    const lines = (b.event || event) === 'leave'
      ? [`Checked out, ${person}.`, ...(b.armed ? ['The alarm is on.'] : [])]
      : [`Checked in, ${person}. The alarm is off while you're here.`];
    if (b.duplicate) lines.push('(That was already recorded a moment ago.)');
    return { tone: 'ok', lines };
  }
  const status = res ? res.status : 0;
  if (status === 401) return { tone: 'bad', lines: ["That PIN isn't an active employee's."] };
  if (status === 429 || status === 409) return { tone: 'bad', lines: [res.message || 'Not recorded. Try again later.'] };
  if (!status) return { tone: 'bad', lines: ['Could not reach JobQ. Nothing was recorded. Try again.'] };
  return { tone: 'bad', lines: [`Not recorded: ${res.message || `HTTP ${status}`}`] };
}

// ── The door alarm on the Cameras pages (GET /security/status `doors`, 2026-10-25) ───────────────
// `doors` is null when the shop has no door system: every helper below then returns nothing.

export const STATUS_POLL_MS = 60000;
export const STATUS_FAST_POLL_MS = 10000;

const isoMs = (iso) => {
  const t = iso ? new Date(iso).getTime() : NaN;
  return Number.isFinite(t) ? t : null;
};

/** The siren is sounding: siren_until is in the future. */
export function sirenSounding(doors, now = new Date()) {
  const t = isoMs(doors && doors.siren_until);
  return t != null && t > now.getTime();
}

/** A hold is pending or the siren is sounding: the status read runs every 10 s instead of 60. */
export function doorsUrgent(doors, now = new Date()) {
  return !!doors && ((doors.holds || []).length > 0 || sirenSounding(doors, now));
}

/** How often to read GET /security/status for this answer. */
export const statusPollMs = (status, now = new Date()) =>
  (doorsUrgent(status && status.doors, now) ? STATUS_FAST_POLL_MS : STATUS_POLL_MS);

/** "1:32" until `iso` (never below 0:00). */
export function countdownText(iso, now = new Date()) {
  const t = isoMs(iso);
  const left = t == null ? 0 : Math.max(0, Math.round((t - now.getTime()) / 1000));
  return `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`;
}

/** One line per pending hold: "NW Entry Door opened 9:14 PM. Alert in 1:32 unless someone checks in". */
export function holdBannerText(hold, now = new Date()) {
  const opened = shopClock(hold.at, now);
  const head = `${hold.name || 'A door'} opened${opened ? ` ${opened}` : ''}.`;
  const t = isoMs(hold.alerts_at);
  if (t != null && t <= now.getTime()) return `${head} Alert going out now unless someone checks in`;
  return `${head} Alert in ${countdownText(hold.alerts_at, now)} unless someone checks in`;
}

/** The pending holds, soonest alert first: [{event_id, zone_id, text}]. */
export function holdBanners(doors, now = new Date()) {
  return [...((doors && doors.holds) || [])]
    .sort((a, b) => (isoMs(a.alerts_at) || 0) - (isoMs(b.alerts_at) || 0))
    .map((h) => ({ event_id: h.event_id, zone_id: h.zone_id, text: holdBannerText(h, now) }));
}

/**
 * The doors line beside the night-watch pill: {tone: 'muted' | 'bad', text} or null (no door system).
 * "6 doors closed · panel online", "North Rollup Door OPEN since 9:14 PM", "2 doors open: A, B",
 * "Door panel offline (last report 9:10 PM)".
 */
export function doorsLine(doors, now = new Date()) {
  if (!doors) return null;
  const panels = doors.panels || [];
  const zones = (doors.zones || []).filter((z) => z.is_active !== false);
  const bad = [];
  const open = zones.filter((z) => z.state === 'open');
  if (open.length === 1) {
    const since = shopClock(open[0].state_since, now);
    bad.push(`${open[0].name} OPEN${since ? ` since ${since}` : ''}`);
  } else if (open.length > 1) {
    bad.push(`${open.length} doors open: ${open.map((z) => z.name).join(', ')}`);
  }
  for (const p of panels.filter((x) => !x.online)) {
    const last = latest(p.last_beat_at, p.last_event_at);
    const who = panels.length > 1 ? `${p.name} offline` : 'Door panel offline';
    bad.push(last ? `${who} (last report ${shopClock(last, now)})` : `${who} (never reported)`);
  }
  if (bad.length) return { tone: 'bad', text: bad.join(' · ') };
  if (!panels.length) return { tone: 'muted', text: 'No door panel set up' };
  const closed = zones.filter((z) => z.state === 'closed').length;
  const unknown = zones.length - closed;
  const doorsPart = !zones.length ? 'No doors named yet'
    : `${closed} door${closed === 1 ? '' : 's'} closed${unknown ? `, ${unknown} unknown` : ''}`;
  return { tone: 'muted', text: `${doorsPart} · panel${panels.length > 1 ? 's' : ''} online` };
}

/**
 * The doors whose alert photo uses this camera (a camera key is the relay's `<slug>-<index>`, the
 * same key a tile has): [{zone_id, name, open, hold}].
 */
export function doorChipsForCamera(doors, cameraKey) {
  if (!doors || !cameraKey) return [];
  const holding = new Set((doors.holds || []).map((h) => h.zone_id));
  return (doors.zones || [])
    .filter((z) => z.is_active !== false && (z.camera_keys || []).includes(cameraKey))
    .map((z) => ({ zone_id: z.zone_id, name: z.name, open: z.state === 'open', hold: holding.has(z.zone_id) }));
}

/** A tile's frame goes red while any of its doors is open or holding an alert. */
export const doorChipsAlarm = (chips) => (chips || []).some((c) => c.open || c.hold);

// ── Door rows in the Cameras page's alert list (GET /security/alerts, source 'door') ──────────────

export const isDoorAlert = (a) => !!a && a.source === 'door';

/** "cleared by a check-in (Brady (timeclock))", from the hold's resolution ("cleared: here: …"). */
function doorClearedText(note) {
  const n = String(note || '').replace(/^cleared:\s*/i, '').trim();
  if (!n) return 'cleared by a check-in';
  if (/^here:\s*/i.test(n)) return `cleared by a check-in (${n.replace(/^here:\s*/i, '')})`;
  return `cleared (${n})`;
}

/** The outcome chip's words for a door alert, in the list's own wording. */
export function doorOutcomeLabel(a) {
  const texted = a.texted || [];
  const pushed = a.pushed || [];
  switch (a.outcome) {
    case 'texted': {
      const who = [texted.length ? `texted ${texted.join(', ')}` : '', pushed.length ? `pushed to ${pushed.join(', ')}` : '']
        .filter(Boolean).join(' · ');
      return who || 'no text went out';
    }
    case 'not_texted':
      return `NOT texted${(a.errors || []).length ? `: ${a.errors.join('; ')}` : ''}`;
    case 'dropped':
      return doorClearedText(a.note);
    default:
      return a.outcome || '';
  }
}

const CLOCK_ONLY = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Chicago', hour: 'numeric', minute: '2-digit' });

/** "Door: North Rollup Door opened · 9:14 PM" (the clock only, like a camera row: the list groups by night). */
export function doorAlertTitle(a) {
  const t = a.seen_at ? new Date(a.seen_at) : null;
  const at = t && !Number.isNaN(t.getTime()) ? CLOCK_ONLY.format(t).replace(/[\u202f\u00a0]/g, ' ') : '';
  return `Door: ${a.camera || 'a door'} opened${at ? ` · ${at}` : ''}`;
}
